import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Hourglass } from 'lucide-react';
import {
  AdvancedFilterButton,
  AdvancedFilterPanel,
  FilterPanelCollapse,
} from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useLocalListSearch } from '@/hooks/useLocalListSearch';
import { getFilterPanelProps } from '@/hooks/useUrlSyncedFilters';
import { getDateFromToFilterFields } from '@/utils/filterFields';
import { getTableEmptyText } from '@/utils/listSearch';
import { pendingSummaryQueryOptions } from '../api/pendingSummary.api';
import { getPendingSummaryColumns } from '../components/PendingSummaryColumns';
import { usePendingSummaryFilters } from '../hooks/usePendingSummaryFilters';
import { PENDING_COLUMNS, type PendingSummaryRow } from '../types/pendingSummary.types';

const PERIOD_FILTER_FIELDS = getDateFromToFilterFields();

const toSearchablePendingValues = (row: PendingSummaryRow) => [row.reportingName];

export function PendingSummaryPage() {
  const controller = usePendingSummaryFilters();
  const { search } = controller;
  const { data, isLoading, isFetching, error, refetch } = useQuery(
    pendingSummaryQueryOptions(search),
  );
  const { searchText, setSearchText, visibleRows } = useLocalListSearch({
    rows: data?.items,
    appliedText: controller.appliedSearchText,
    onApply: controller.applySearchText,
    toSearchableValues: toSearchablePendingValues,
  });
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const columns = useMemo(
    () => getPendingSummaryColumns(controller.appliedSearchText),
    [controller.appliedSearchText],
  );
  const totals = data?.meta.totals;

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="DCR & DER Pending"
        titleAddon={
          <DateRangeBadge
            onClick={() => setIsFilterOpen(true)}
            dateFrom={search.dateFrom}
            dateTo={search.dateTo}
          />
        }
        search={{ value: searchText, onChange: setSearchText, placeholder: 'Reporting name' }}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <AdvancedFilterPanel fields={PERIOD_FILTER_FIELDS} {...getFilterPanelProps(controller)} />
      </FilterPanelCollapse>

      {error ? (
        <ErrorState
          error={error}
          fallbackTitle="Could not load the pending summary"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid
            isLoading={isLoading}
            isFetching={isFetching}
            stats={[
              {
                key: 'entryPersonQty',
                label: 'Entry Persons',
                value: totals?.entryPersonQty,
              },
              ...PENDING_COLUMNS.map((column) => ({
                ...column,
                value: totals?.[column.key],
              })),
              {
                key: 'total',
                label: 'Total',
                value: totals?.total,
              },
            ]}
          />

          <DataTable
            data={visibleRows}
            columns={columns}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="reportingName"
            emptyIcon={Hourglass}
            {...getTableEmptyText(
              controller.appliedSearchText,
              'Nothing is pending',
              'No DCR or DER is waiting in this fiscal year.',
            )}
          />
        </>
      )}
    </div>
  );
}
